import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface ChatThread {
  id: string;
  title?: string;
  createdAt: any;
  lastMessageAt: any;
}

const History: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [history, setHistory] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      if (!currentUser) return;

      console.log('Fetching history for user:', currentUser.uid);

      const threadsRef = collection(db, 'users', currentUser.uid, 'threads');
      const q = query(threadsRef, orderBy('lastMessageAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const threads = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Thread data:', { id: doc.id, ...data });
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt,
          lastMessageAt: data.lastMessageAt
        };
      }) as ChatThread[];

      console.log('Fetched threads:', threads);
      setHistory(threads);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      console.log('Current user changed, fetching history');
      fetchHistory();
    }
  }, [currentUser]);

  const handleClearHistory = async () => {
    try {
      if (!currentUser) return;
      
      // Delete each thread document
      for (const thread of history) {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'threads', thread.id));
      }
      
      setHistory([]);
      setClearDialogOpen(false);
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      if (!currentUser) return;
      
      await deleteDoc(doc(db, 'users', currentUser.uid, 'threads', id));
      setHistory((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    
    try {
      // If it's a Firestore Timestamp
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return formatDistanceToNow(timestamp.toDate(), { addSuffix: true });
      }
      
      // If it's a timestamp in seconds
      if (timestamp.seconds) {
        return formatDistanceToNow(new Date(timestamp.seconds * 1000), { addSuffix: true });
      }
      
      // If it's a Date object
      if (timestamp instanceof Date) {
        return formatDistanceToNow(timestamp, { addSuffix: true });
      }

      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const handleThreadClick = (thread: ChatThread) => {
    navigate('/chat', { state: { threadId: thread.id } });
  };

  const handleRefresh = () => {
    fetchHistory();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.army.textPrimary,
              mb: 2,
            }}
          >
            Chat History
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.army.textSecondary,
            }}
          >
            Your recent AI conversations
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ color: theme.palette.army.accent }}
          >
            Refresh
          </Button>
          {history.length > 0 && (
            <Button
              color="error"
              onClick={() => setClearDialogOpen(true)}
              sx={{ color: theme.palette.army.accent }}
            >
              Clear History
            </Button>
          )}
        </Box>
      </Box>

      {history.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: theme.palette.army.main,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <ChatIcon
              sx={{
                fontSize: 32,
                color: theme.palette.army.accent,
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.army.textPrimary,
              mb: 1,
            }}
          >
            No Chat History
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.army.textSecondary,
            }}
          >
            Your conversations with the AI will appear here
          </Typography>
        </Paper>
      ) : (
        <List>
          {history.map((thread) => (
            <Paper
              key={thread.id}
              sx={{
                mb: 2,
                bgcolor: theme.palette.army.main,
                '&:hover': {
                  bgcolor: theme.palette.army.shield,
                  cursor: 'pointer',
                },
              }}
              onClick={() => handleThreadClick(thread)}
            >
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteItem(thread.id);
                    }}
                    sx={{ color: theme.palette.army.accent }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ChatIcon sx={{ color: theme.palette.army.accent, mr: 1 }} />
                    <Typography
                      variant="h6"
                      sx={{ color: theme.palette.army.textPrimary }}
                    >
                      {thread.title || `Chat ${formatDate(thread.createdAt)}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`Created ${formatDate(thread.createdAt)}`}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.army.shield,
                        color: theme.palette.army.textPrimary,
                      }}
                    />
                    <Chip
                      label={`Last message ${formatDate(thread.lastMessageAt)}`}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.army.accent,
                        color: theme.palette.army.textPrimary,
                      }}
                    />
                  </Box>
                </Box>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.army.main,
          },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.army.textPrimary }}>
          Clear History
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.army.textSecondary }}>
            Are you sure you want to clear your entire chat history? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearDialogOpen(false)}
            sx={{ color: theme.palette.army.accent }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearHistory}
            sx={{
              bgcolor: theme.palette.army.accent,
              color: theme.palette.army.textPrimary,
              '&:hover': {
                bgcolor: theme.palette.army.shield,
              },
            }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default History; 